/**
 * Générateur — Automatisme : utiliser la réciproque du théorème de Pythagore
 * pour démontrer qu'un triangle est rectangle à partir des longueurs de ses
 * côtés.
 * Attendu ID : 1089 | Niveau : 4ème | Domaine : Espace et géométrie, géométrie plane
 * Rubrique : AUTOMATISME (créé par le professeur, gen neuf du 2026-09-24).
 *
 * ── D'où vient l'intitulé ─────────────────────────────────────────────────
 * Au programme 2025, la réciproque est un OBJECTIF de 4e (cycle 4 l. 1041
 * « Connaitre le théorème de Pythagore, sa réciproque, sa contraposée »,
 * l. 1042 « Mener un travail de logique sur la réciproque et la
 * contraposée »), pas un automatisme. Ligne servie, qui fait plafond :
 * Eduscol, attendus de fin de 4e (2019) l. 428-429 « Il démontre qu'un
 * triangle est un triangle rectangle à partir de la connaissance des
 * longueurs de ses côtés. », exemple de réussite l. 430-431 (l'étagère
 * d'Alan : RS = 42, TR = 40, ST = 58). La mise en œuvre 2026 attend le mot
 * « contraposée » ; le cours du prof ne l'emploie pas et c'est le cours qui
 * fait foi (décision prof 24/09) : « L'égalité de Pythagore n'est pas
 * vérifiée donc le triangle CDE n'est pas rectangle. »
 *
 * ── Cadrage (sous-agent programme-maths 2026-09-24, décisions prof) ───────
 * Les DEUX sens : rectangle un tirage sur deux (ni « toujours non » ni « le
 * sommet en face du plus grand côté » ne rapportent plus d'une réponse sur
 * deux). Rectangles : les triplets de 1088 (côtés ≤ 30, jamais (5 ; 12 ;
 * 13)). Non rectangles : un triplet dont UN côté est décalé de 1 ou de 2
 * (comme le 10,3 du prof au lieu de 10,4), triangle constructible, un seul
 * plus grand côté, jamais un autre triplet. Pas de calcul de racine : des
 * carrés à calculer et à comparer. CALCULATRICE AUTORISÉE sur « Rectangle ou
 * pas ? », « Les deux calculs » et « Droit ou pas ? » (décision prof 24/09 au
 * soir, « n'oublie pas élève de 4e » : 29², 58², 150² ne se font pas de tête),
 * pas sur « Les étapes » (les calculs y sont donnés). Rappel brouillon
 * IMPÉRATIF sur les trois cartes de calcul depuis le 25/09 ; pas sur « Les
 * étapes dans l'ordre » (prof 25/09, comme 1087 : rien à calculer, on range ;
 * exception à la règle par attendu, décidée carte par carte). PAS DE FIGURE
 * dans les cartes de calcul (« à partir des longueurs » ; une figure
 * exacte montrerait la réponse) ; la carte de contexte a une figure exacte
 * SANS codage, l'écart à l'angle droit (quelques degrés) ne se voit pas.
 * SACRIFIÉS (fiche à venir) : (5 ; 12 ; 13)
 * rectangle et (7 ; 12 ; 15) non rectangle (cours du prof), jamais tirés.
 *
 * ── Révision 2026-09-25 (retour prof, carte par carte) ───────────────────
 *   - Brouillon impératif (RAPPEL_BROUILLON, juste avant la question) sur les
 *     trois cartes de calcul ; pas sur « Les étapes dans l'ordre ».
 *   - « La conclusion » SUPPRIMÉE (prof : « inutile, on a déjà une autre
 *     carte à côté qui le fait bien ») : « Droit ou pas ? » conclut déjà,
 *     avec la justification.
 *   - « Rectangle ou pas ? » GARDÉE : elle demande en quel sommet, ce que
 *     « Droit ou pas ? » ne fait pas.
 *
 * ── Cartes ────────────────────────────────────────────────────────────────
 *   « Rectangle ou pas ? » (rectangle_qcm_unique) : trois longueurs, « Oui,
 *     il est rectangle en K / M / T » ou « Non » (l'ancienne carte 1 de
 *     gen_190, sans son indication « Utilise la réciproque… », à un sur deux).
 *   « Les deux calculs » (calculs_texte_trou_saisie) : le plus grand côté est
 *     donné, « D'une part : MT² = ⬚ » et « D'autre part : KM² + KT² = ⬚ »
 *     (le cours, 19Pyth2).
 *   « Les étapes dans l'ordre » (etapes_ordonner) : les cinq lignes du cours,
 *     rectangle ou non ; « D'une part » / « D'autre part » fixent l'ordre.
 *   « Droit ou pas ? » (droit_qcm_unique) : l'étagère (Eduscol) ou le mur des
 *     maçons (fiche 8 du prof), figure exacte sans codage ; quatre réponses
 *     justifiées, deux « oui » et deux « non ».
 *
 * ── Erreurs (familles nommées) ───────────────────────────────────────────
 *   direct_pour_reciproque (PRINCIPALE) : « d'après le théorème de
 *     Pythagore, il est rectangle » (DEPP début de 2de 2025 q.18 : 24,7 %) ;
 *   sommet_sur_hypotenuse : l'angle droit placé à une extrémité du plus grand
 *     côté ;
 *   conclusion_inversee : égal, « pas rectangle » ; différent, « rectangle » ;
 *   reciproque_pour_conclure_non : « d'après la réciproque, il n'est pas
 *     rectangle » (la réciproque ne dit rien quand l'égalité est fausse) ;
 *   sans_carres : comparer les longueurs (58 et 42 + 40) au lieu des carrés.
 *   Écartées : « n'est pas rectangle en K » (vrai quand il n'est rectangle
 *   nulle part : une phrase juste comptée fausse) ; « d'après le théorème de
 *   Pythagore, il n'est pas rectangle » (c'est la contraposée du théorème,
 *   un raisonnement valable).
 *
 * Hors : calculer une longueur (1088) ; écrire l'égalité (1243, 3e) ; les cas
 * à calculatrice du prof (TOC 77/35/85, MNP 9,6/4/10,3) et le losange OELM
 * (deux propriétés) : 190 ; choisir le théorème : 190.
 */
import {
    randInt, randomChoice, shuffle, pickLettresSains, tex,
    makeQcm, makeTexteTrou, makeOrdonner,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { svgEtagere, svgMur } from './figContextes';

const ATTENDU_ID = 1089;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_RECTANGLE = 'Rectangle ou pas ?';
const L_CALCULS = 'Les deux calculs';
const L_ETAPES = 'Les étapes dans l\'ordre';
const L_DROIT = 'Droit ou pas ?';

// Calculatrice AUTORISÉE sur les cartes de calcul (décision prof 2026-09-24 au soir : « n'oublie pas élève de 4e »).
const calculatrice = (payload) => ({ ...payload, contenu: { ...payload.contenu, calculatrice: true } });
// Rappel brouillon (posé le 2026-09-25) : IMPÉRATIF sur les trois cartes de calcul, juste avant la
// question ; pas sur « Les étapes dans l'ordre » (prof : rien à calculer, les calculs sont donnés).
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';
const POOL = 'ABCDEFGHJKLMNPRSTUV'.split('');
const UNITES = ['cm', 'cm', 'mm', 'm'];
const PRENOMS = ['Léna', 'Youri', 'Sacha', 'Nina', 'Timéo', 'Awa', 'Malo', 'Jade', 'Inès', 'Noam'];

/** Rectangles : les triplets de 1088 (côtés ≤ 30), jamais (5 ; 12 ; 13). */
export const TRIPLETS = [
    [3, 4, 5], [6, 8, 10], [9, 12, 15], [12, 16, 20], [15, 20, 25], [18, 24, 30],
    [8, 15, 17], [7, 24, 25], [10, 24, 26], [20, 21, 29],
];
/** Les triangles des fiches coup de pouce (rectangle, non rectangle) : jamais tirés. */
export const SACRIFIES = [[5, 12, 13], [7, 12, 15]];
const memes = (x, y) => [...x].sort((p, q) => p - q).join(',') === [...y].sort((p, q) => p - q).join(',');
const estSacrifie = (cotes) => SACRIFIES.some((s) => memes(s, cotes));

/** Vrai si a² + b² = c² pour le plus grand côté c (entiers). */
export const estRectangle = (cotes) => {
    const [a, b, c] = [...cotes].sort((p, q) => p - q);
    return a * a + b * b === c * c;
};

/**
 * Trois longueurs entières : un triplet (rectangle) ou un triplet dont un
 * côté est décalé de ±1 ou ±2 (non rectangle), constructible, un seul plus
 * grand côté, pas rectangle, jamais sacrifié.
 */
const tirerLongueurs = (rectangle) => {
    for (let essai = 0; essai < 200; essai++) {
        const t = randomChoice(TRIPLETS);
        if (rectangle) return shuffle(t);
        const i = randInt(0, 2); const delta = randomChoice([-2, -1, 1, 2]);
        const c = t.map((v, j) => (j === i ? v + delta : v));
        const [p, q, r] = [...c].sort((x, y) => x - y);
        if (p < 2 || p + q <= r || q === r || estRectangle(c) || estSacrifie(c) || c.some((v) => v > 30)) continue;
        return shuffle(c);
    }
    return [6, 8, 11];                                          // repli vérifié : 36 + 64 = 100 ≠ 121
};

const cote = (p, q) => [p, q].sort().join('');

/**
 * Un triangle nommé et ses trois longueurs ; `oppose` = le sommet en face du
 * plus grand côté ; les côtés sont nommés dans l'ordre alphabétique.
 */
const tirer = (rectangle) => {
    const lettres = pickLettresSains(POOL, 3);
    const nom = lettres.join('');
    const cotesNom = [cote(lettres[0], lettres[1]), cote(lettres[1], lettres[2]), cote(lettres[0], lettres[2])];
    const valeurs = tirerLongueurs(rectangle);
    const longueurs = {}; cotesNom.forEach((k, i) => { longueurs[k] = valeurs[i]; });
    const grand = cotesNom.reduce((m, k) => (longueurs[k] > longueurs[m] ? k : m));
    const autres = cotesNom.filter((k) => k !== grand).sort();
    const oppose = lettres.find((l) => !grand.includes(l));
    const unite = randomChoice(UNITES);
    // l'ordre de l'énoncé : le plus grand côté n'est pas toujours le dernier
    const ordre = shuffle([...cotesNom]);
    const gauche = longueurs[grand] ** 2; const droite = longueurs[autres[0]] ** 2 + longueurs[autres[1]] ** 2;
    return { lettres, nom, longueurs, grand, autres, oppose, unite, ordre, rectangle: gauche === droite, gauche, droite };
};

const donnees = (t) => `<p>Le triangle ${tex(t.nom)} a pour côtés `
    + `${tex(`${t.ordre[0]} = ${t.longueurs[t.ordre[0]]}`)} ${t.unite}, ${tex(`${t.ordre[1]} = ${t.longueurs[t.ordre[1]]}`)} ${t.unite} `
    + `et ${tex(`${t.ordre[2]} = ${t.longueurs[t.ordre[2]]}`)} ${t.unite}.</p>`;

// ─── Carte « Rectangle ou pas ? » (QCM, sans figure) ──────────────────────

const genRectangle = () => {
    const t = tirer(randInt(0, 1) === 0);
    const choix = [
        ...t.lettres.map((l) => `Oui, il est rectangle en ${tex(l)}.`),
        'Non, il n\'est pas rectangle.',
    ];
    const bon = t.rectangle ? `Oui, il est rectangle en ${tex(t.oppose)}.` : 'Non, il n\'est pas rectangle.';
    const texte = donnees(t) + RAPPEL_BROUILLON + '<p><b>Ce triangle est-il rectangle ?</b></p>';
    const payload = makeQcm(choix, bon, texte, opts(L_RECTANGLE));
    payload._meta.params = {
        longueurs: t.longueurs, rectangle: t.rectangle, oppose: t.oppose,
        distracteurs: [{ code: 'sommet_sur_hypotenuse' }, { code: 'conclusion_inversee' }],
    };
    return calculatrice(payload);
};

// ─── Carte « Les deux calculs » (saisie guidée, sans figure) ──────────────

const genCalculs = () => {
    const t = tirer(randInt(0, 1) === 0);
    const [k1, k2] = t.autres;
    const lignes = [
        `$\\text{D'une part : } ${t.grand}^{2} = {{${t.gauche}}}$`,
        `$\\text{D'autre part : } ${k1}^{2} + ${k2}^{2} = {{${t.droite}}}$`,
    ];
    const texte = donnees(t) + `<p>Son plus grand côté est ${tex(`[${t.grand}]`)}.</p>`
        + RAPPEL_BROUILLON + '<p><b>Complète les deux calculs.</b></p>';
    const payload = makeTexteTrou(lignes.join('\n'), texte, { ...opts(L_CALCULS), mode: 'saisie' });
    payload._meta.params = { longueurs: t.longueurs, grand: t.grand, gauche: t.gauche, droite: t.droite, rectangle: t.rectangle };
    return calculatrice(payload);
};

// ─── Carte « Les étapes dans l'ordre » (ordonner, sans figure) ────────────

const genEtapes = () => {
    const t = tirer(randInt(0, 1) === 0);
    const [k1, k2] = t.autres;
    const g = t.longueurs[t.grand]; const v1 = t.longueurs[k1]; const v2 = t.longueurs[k2];
    const etapes = [
        `Le plus grand côté est ${tex(`[${t.grand}]`)}.`,
        `D'une part : ${tex(`${t.grand}^{2} = ${g}^{2} = ${t.gauche}`)}.`,
        `D'autre part : ${tex(`${k1}^{2} + ${k2}^{2} = ${v1}^{2} + ${v2}^{2} = ${t.droite}`)}.`,
        t.rectangle
            ? `Donc ${tex(`${t.grand}^{2} = ${k1}^{2} + ${k2}^{2}`)}.`
            : `Donc ${tex(`${t.grand}^{2} \\neq ${k1}^{2} + ${k2}^{2}`)}.`,
        t.rectangle
            ? `D'après la réciproque du théorème de Pythagore, le triangle ${tex(t.nom)} est rectangle en ${tex(t.oppose)}.`
            : `L'égalité de Pythagore n'est pas vérifiée donc le triangle ${tex(t.nom)} n'est pas rectangle.`,
    ];
    // Pas de rappel brouillon ici (prof 25/09, comme 1087) : les calculs sont donnés, on range.
    const texte = donnees(t) + '<p>Voici, dans le désordre, les étapes pour savoir s\'il est rectangle.</p>'
        + '<p><b>Remets les étapes dans l\'ordre.</b></p>';
    const payload = makeOrdonner(etapes, texte, opts(L_ETAPES));
    payload._meta.params = { longueurs: t.longueurs, rectangle: t.rectangle, oppose: t.oppose };
    return payload;
};

// ─── Carte « Droit ou pas ? » (QCM, figure exacte sans codage) ────────────

/** Étagères (cm) : [RS sur le mur, RT l'étagère, ST le support], ST² = RS² + RT² pour les rectangles. */
export const ETAGERES = [
    [42, 40, 58], [24, 18, 30], [20, 15, 25], [21, 20, 29], [28, 21, 35], [32, 24, 40], [16, 12, 20], [30, 16, 34], [36, 27, 45], [40, 30, 50],
    [45, 28, 53], [48, 36, 60], [56, 33, 65], [60, 45, 75],
];
/** Murs (cm) : [IH sur le mur, IS au sol, HS au mètre ruban], HS² = IH² + IS² pour les droits. */
export const MURS = [[60, 80, 100], [30, 40, 50], [45, 60, 75], [90, 120, 150], [36, 48, 60], [48, 64, 80], [80, 60, 100], [120, 90, 150]];

/** L'angle (en degrés) au coin d'un triangle de côtés a et b autour du coin, c en face. */
export const angleAuCoin = (a, b, c) => (Math.acos((a * a + b * b - c * c) / (2 * a * b)) * 180) / Math.PI;

/**
 * La mesure en biais d'un « non » : décalée de 1 à 5 cm, de sorte que l'angle
 * au coin s'écarte de l'angle droit de 1° à 2,5° (invisible sur la figure : le
 * calcul tranche ; à 5° le mur penchait à l'œil, vu en PNG) ; null si aucun
 * décalage ne convient (les petits triangles ne servent qu'aux « oui »).
 */
const decaler = (a, b, c) => {
    const bons = [-5, -4, -3, -2, -1, 1, 2, 3, 4, 5].map((k) => c + k)
        .filter((c2) => c2 > Math.max(a, b) && a * a + b * b !== c2 * c2)
        .filter((c2) => { const e = Math.abs(angleAuCoin(a, b, c2) - 90); return e >= 1 && e <= 2.5; });
    return bons.length ? randomChoice(bons) : null;
};

const genDroit = () => {
    const contexte = randomChoice(['etagere', 'mur']);
    const droit = randInt(0, 1) === 0;
    let a; let b; let c;
    for (let essai = 0; essai < 50; essai++) {
        [a, b, c] = randomChoice(contexte === 'etagere' ? ETAGERES : MURS);
        if (droit) break;
        const c2 = decaler(a, b, c);
        if (c2 !== null) { c = c2; break; }
    }
    if (!droit && a * a + b * b === c * c) { [a, b, c] = [42, 40, 57]; }   // repli vérifié : 1764 + 1600 = 3364 ≠ 3249, angle 88°
    const [coin, bas, bout] = pickLettresSains(POOL, 3);   // coin : le sommet de l'angle en question
    const nA = cote(coin, bas); const nB = cote(coin, bout); const nC = cote(bas, bout);
    const mesures = { [nA]: `${a} cm`, [nB]: `${b} cm`, [nC]: `${c} cm` };
    const prenom = randomChoice(PRENOMS);
    let donneesTxt; let question; let svg;
    if (contexte === 'etagere') {
        donneesTxt = `${prenom} a posé une étagère ${tex(`[${nB}]`)} sur un mur vertical, avec un support ${tex(`[${nC}]`)} ; `
            + `les points ${tex(coin)} et ${tex(bas)} sont sur le mur. On mesure ${tex(`${nA} = ${a}`)} cm, ${tex(`${nB} = ${b}`)} cm et ${tex(`${nC} = ${c}`)} cm.`;
        question = 'L\'étagère est-elle horizontale ?';
        svg = svgEtagere({ a, b, c, lettres: { coin, bas, bout }, mesures });
    } else {
        donneesTxt = `Pour vérifier qu'un mur est droit, ${prenom} marque un point ${tex(bas)} sur le mur, à ${a} cm du point ${tex(coin)} au pied du mur, `
            + `et un point ${tex(bout)} sur le sol, à ${b} cm de ${tex(coin)}. Au mètre ruban, ${prenom} trouve ${tex(`${nC} = ${c}`)} cm.`;
        question = 'Le mur est-il perpendiculaire au sol ?';
        svg = svgMur({ a, b, c, lettres: { pied: coin, haut: bas, sol: bout }, mesures });
    }
    const T = tex(`${bas}${coin}${bout}`);
    const choix = [
        { v: `Oui, car ${tex(`${nC}^{2} = ${nA}^{2} + ${nB}^{2}`)} : d'après la réciproque du théorème de Pythagore, le triangle ${T} est rectangle en ${tex(coin)}.`, cle: droit },
        { v: `Non, car ${tex(`${nC}^{2} \\neq ${nA}^{2} + ${nB}^{2}`)} : l'égalité de Pythagore n'est pas vérifiée, le triangle ${T} n'est pas rectangle.`, cle: !droit },
        { v: `Oui, d'après le théorème de Pythagore.`, cle: false, code: 'direct_pour_reciproque' },
        { v: `Non, car ${tex(`${nC} \\neq ${nA} + ${nB}`)}.`, cle: false, code: 'sans_carres' },
    ];
    const bon = choix.find((x) => x.cle).v;
    const texte = `<p>${donneesTxt}</p>${RAPPEL_BROUILLON}<p><b>${question} Choisis la réponse juste et bien justifiée.</b></p>`;
    const payload = makeQcm(choix.map((x) => x.v), bon, texte, { ...opts(L_DROIT), elements_additionnels: imgElement(svg, 'large') });
    payload._meta.params = {
        contexte, droit, mesures: [a, b, c], coin,
        distracteurs: [{ code: 'direct_pour_reciproque' }, { code: 'sans_carres' }, { code: 'conclusion_inversee' }],
    };
    return calculatrice(payload);
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'rectangle un tirage sur deux ; non rectangle = un triplet dont un côté est décalé de ±1 ou ±2, constructible, un seul plus grand côté, jamais un triplet, jamais (5 ; 12 ; 13) ni (7 ; 12 ; 15)',
        'pas de figure dans les cartes de calcul ; lettres saines, côtés nommés dans l\'ordre alphabétique, tout en KaTeX',
    ],
    rectangle_qcm_unique: 'positions fixes : les trois sommets dans l\'ordre du nom puis « Non » ; sommet_sur_hypotenuse (les deux sommets du plus grand côté), conclusion_inversee',
    supprimees: { conclusion_qcm_unique: 'sortie le 2026-09-25 (prof) : « Droit ou pas ? » conclut déjà, avec la justification' },
    droit_qcm_unique: 'quatre réponses fixes : oui par la réciproque, non par l\'égalité non vérifiée, direct_pour_reciproque (« oui, d\'après le théorème »), sans_carres (« non, car ST ≠ RS + RT ») ; la mesure en biais décalée de 1 à 5 cm pour un « non », l\'angle au coin à 1° à 2,5° de l\'angle droit (invisible sur la figure), les petits triangles réservés aux « oui »',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    rectangle_qcm_unique: genRectangle,
    calculs_texte_trou_saisie: genCalculs,
    etapes_ordonner: genEtapes,
    droit_qcm_unique: genDroit,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', texte_trou_saisie: 'Saisie guidée', ordonner: 'Ordonner' };
const buildFormats = (prefix) => Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
    .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'rectangle', label: L_RECTANGLE, formatsList: buildFormats('rectangle') },
    { id: 'calculs', label: L_CALCULS, formatsList: buildFormats('calculs') },
    { id: 'etapes', label: L_ETAPES, formatsList: buildFormats('etapes') },
    { id: 'droit', label: L_DROIT, formatsList: buildFormats('droit') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
